import React, { useState, useMemo, useEffect, useContext, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { useHistory, useLocation } from 'react-router-dom';
import { FirebaseAuth } from "../FirebaseAuth/firebase";
import { AppBar as MuiAppBar, Avatar, Toolbar, CssBaseline, Divider, IconButton, Box, Grid, Button, Menu, MenuItem, ListItemText } from '@mui/material';
// import Brightness7Icon from '@mui/icons-material/Brightness7';
// import Brightness4Icon from '@mui/icons-material/Brightness4';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import Logo from '../Logo';
import Brand from '../Brand';
// import Loader from '../Loader';
import { BreadcrumbContext } from '../Breadcrumb';
import { DashboardChromeProvider } from '../DashboardChrome';
import DashboardSearchPalette from '../Dashboard/DashboardSearchPalette';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { AuthContext } from '../FirebaseAuth';

const APP_BAR_HEIGHT = 76;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 2,
  minHeight: `${APP_BAR_HEIGHT}px`,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: '0px',
    width: '100%',
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Layout = ({ userMenu, children}) => {
  const history = useHistory();
  const location = useLocation();
  const { authUser } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const mode = 'light';
  const [, setBreadcrumb] = useState([]);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const [accountSwitcherLoading, setAccountSwitcherLoading] = useState(false);
  const [clientAccountOptions, setClientAccountOptions] = useState([]);
  const [activeClientAccountId, setActiveClientAccountId] = useState("");
  const [activeClientAccountName, setActiveClientAccountName] = useState("");
  const [searchPaletteOpen, setSearchPaletteOpen] = useState(false);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          background: {
            default: mode === 'dark' ? '#121212' : '#fafafa',
            paper: mode === 'dark' ? '#1e1e1e' : '#ffffff',
          },
          text: mode === 'dark'
            ? {
                primary: '#e0e0e0',
                secondary: '#bdbdbd',
                disabled: '#757575',
              }
            : {
                primary: '#424242',
                secondary: '#616161',
                disabled: '#9e9e9e',
              },
          mode,
        },
        typography: {
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
        },
        breakpoints: {
          values: {
            xs: 0,
            sm: 480,
            md: 960,
            lg: 1440,
            xl: 1920,
          },
        },
        components: {
          MuiButton: {
            defaultProps: {
              variant: 'outlined',
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                borderWidth: 1,
                borderStyle: 'solid',
                padding: '1px 6px',
                height: 'auto',
                fontSize: '12px',
                '& .MuiChip-icon': {
                  color: 'inherit',
                },
              },
              label: {
                paddingLeft: 0,
                paddingRight: 0,
              },
            },
            variants: [
              {
                props: { color: 'default' },
                style: {
                  backgroundColor: '#f5f5f5',
                  color: '#3d3d3d',
                  borderColor: '#dddddd',
                },
              },
              {
                props: { color: 'success' },
                style: {
                  backgroundColor: '#cef6bb',
                  color: '#05690D',
                  borderColor: '#b4e1a2',
                },
              },
              {
                props: { color: 'warning' },
                style: {
                  backgroundColor: '#ffe7be',
                  color: '#7a4a00',
                  borderColor: '#f4d19b',
                },
              },
              {
                props: { color: 'info' },
                style: {
                  backgroundColor: '#dcefff',
                  color: '#0d4f8f',
                  borderColor: '#bfdcf6',
                },
              },
              {
                props: { color: 'error' },
                style: {
                  backgroundColor: '#ffd8d8',
                  color: '#8b1c1c',
                  borderColor: '#f0bbbb',
                },
              },
              {
                props: { color: 'primary' },
                style: {
                  backgroundColor: '#e5e9ff',
                  color: '#2f3d8f',
                  borderColor: '#cbd3f6',
                },
              },
              {
                props: { color: 'secondary' },
                style: {
                  backgroundColor: '#f0e5ff',
                  color: '#5b2f8f',
                  borderColor: '#dcc7f6',
                },
              },
            ],
          },
        },
      }),
    [mode],
  );

  const isClientRole = String(authUser?.role || "").toLowerCase() === "client";
  const normalizedRole = String(authUser?.role || "").toLowerCase();
  const searchEnabled = ["admin", "client"].includes(normalizedRole);
  const normalizedPath = String(location?.pathname || "").replace(/\/+$/, "") || "/";
  const isClientDashboardRoute = normalizedPath === "/client/dashboard" || normalizedPath === "/client/dashboard/search";
  const isDashboardRoute = [
    "/client/dashboard",
    "/client/dashboard/search",
    "/contractor/dashboard",
    "/admin/dashboard",
    "/admin/dashboard/search",
    "/user/profile",
    "/user/security",
    "/user/log",
    "/mfa/enroll",
    "/mfa/verify",
  ].includes(normalizedPath);
  const accountMenuOpen = Boolean(accountMenuAnchor);
  const userMenuOpen = Boolean(userMenuAnchor);

  const loadClientAccounts = useCallback(async () => {
    if (!isClientRole || !authUser?.user?.uid) {
      setClientAccountOptions([]);
      setActiveClientAccountId("");
      setActiveClientAccountName("");
      return;
    }
    setAccountSwitcherLoading(true);
    try {
      const userSnap = await FirebaseAuth.firestore().collection("users").doc(authUser.user.uid).get();
      const userData = userSnap.exists ? userSnap.data() || {} : {};
      const accountIds = Array.isArray(userData.account_ids) ? userData.account_ids.filter(Boolean) : [];
      const activeId = String(userData.active_account_id || "").trim();
      setActiveClientAccountId(activeId);

      const uniqueAccountIds = Array.from(new Set([...accountIds, activeId].filter(Boolean)));
      const settled = await Promise.allSettled(
        uniqueAccountIds.map(async (accountId) => {
          const accSnap = await FirebaseAuth.firestore().collection("accounts").doc(accountId).get();
          const accData = accSnap.exists ? accSnap.data() || {} : {};
          return {
            id: accountId,
            name: String(accData.company_name || accData.name || accountId).trim(),
          };
        })
      );

      const accountRows = settled
        .filter((result) => result.status === "fulfilled")
        .map((result) => result.value);

      const failures = settled.filter((result) => result.status === "rejected");
      if (failures.length > 0) {
        console.warn("[Layout] Some client accounts could not be loaded.", {
          uid: authUser.user.uid,
          totalRequested: uniqueAccountIds.length,
          failed: failures.length,
          reasons: failures.map((result) => String(result.reason?.message || result.reason || "unknown error")),
        });
      }

      setClientAccountOptions(accountRows);
      const activeRow = accountRows.find((row) => row.id === activeId);
      setActiveClientAccountName(activeRow?.name || "");
    } catch (error) {
      console.error("[Layout] Failed to load client accounts.", {
        uid: authUser?.user?.uid || "",
        role: authUser?.role || "",
        error: String(error?.message || error || "unknown error"),
      });
      setClientAccountOptions([]);
      setActiveClientAccountName("");
    } finally {
      setAccountSwitcherLoading(false);
    }
  }, [authUser, isClientRole]);

  useEffect(() => {
    loadClientAccounts();
  }, [loadClientAccounts]);

  const openAccountMenu = (event) => {
    setAccountMenuAnchor(event.currentTarget);
  };

  const closeAccountMenu = () => {
    setAccountMenuAnchor(null);
  };

  const openUserMenu = useCallback((event) => {
    setOpen(false);
    setUserMenuAnchor(event?.currentTarget || null);
  }, []);

  const closeUserMenu = useCallback(() => {
    setUserMenuAnchor(null);
  }, []);

  const openSearchPalette = useCallback(() => {
    if (searchEnabled) {
      setSearchPaletteOpen(true);
    }
  }, [searchEnabled]);

  const closeSearchPalette = useCallback(() => {
    setSearchPaletteOpen(false);
  }, []);

  const handleSwitchClientAccount = async (accountId) => {
    if (!accountId || accountId === activeClientAccountId) {
      closeAccountMenu();
      return;
    }
    try {
      const switchClientAccount = FirebaseAuth.functions().httpsCallable("switchClientAccount");
      await switchClientAccount({ account_id: accountId });
      closeAccountMenu();
      window.location.reload();
    } catch (_error) {
      closeAccountMenu();
    }
  };

  const userMenuNode = React.isValidElement(userMenu)
    ? React.cloneElement(userMenu, { onAction: closeUserMenu })
    : userMenu;

  const accountSwitcher = useMemo(
    () => isClientRole && isClientDashboardRoute ? (
      <Box sx={{ ml: 1, mr: 1 }}>
        <Button
          variant="text"
          size="small"
          onClick={openAccountMenu}
          startIcon={<FolderOutlinedIcon sx={{ fontSize: 22 }} />}
          endIcon={<ExpandMoreIcon fontSize="small" />}
          disabled={accountSwitcherLoading || !clientAccountOptions.length}
          sx={{
            textTransform: "none",
            minWidth: 180,
            maxWidth: 320,
            minHeight: 40,
            justifyContent: "space-between",
            color: "#283445",
            bgcolor: "transparent",
            border: "2px solid transparent",
            borderRadius: "14px",
            px: 1.5,
            gap: 0.75,
            "& .MuiButton-startIcon": {
              marginRight: 1,
            },
            "& .MuiButton-endIcon": {
              marginLeft: 0.75,
            },
            "& .MuiSvgIcon-root": {
              color: "#283445",
            },
            "&:hover": {
              bgcolor: "rgba(243, 242, 241, 0.8)",
              borderColor: "#bcbcbc",
            },
            "@media (max-width: 640px)": {
              border: "none",
              borderRadius: 0,
              px: 0,
              bgcolor: "transparent",
              minWidth: 130,
            },
          }}
        >
          <Box
            component="span"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              textAlign: "left",
              display: "block",
              width: "100%",
              fontSize: "14px",
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            {activeClientAccountName || "Select account"}
          </Box>
        </Button>
      </Box>
    ) : null,
    [activeClientAccountName, accountSwitcherLoading, clientAccountOptions.length, isClientDashboardRoute, isClientRole]
  );

  const dashboardChrome = useMemo(
    () => ({
      accountSwitcher,
      searchEnabled,
      openSearchPalette,
      openUserMenu,
    }),
    [accountSwitcher, openSearchPalette, openUserMenu, searchEnabled]
  );

  return (
      <ThemeProvider theme={theme}>
        <Box sx={{ display: 'flex', '--app-bar-height': `${APP_BAR_HEIGHT}px` }}>
          <CssBaseline />
          {!isDashboardRoute && (
          <AppBar position="fixed" open={open} style={{ backgroundColor: theme.palette.mode === 'light' ? '#F3F2F1':'#212121' }}>
            <Toolbar style={{paddingLeft: '8px', paddingRight: '8px', minHeight: `${APP_BAR_HEIGHT}px`}}>
              <Grid container spacing={0} sx={{ width: 'auto' }}>
                <Grid item display={{ xs: 'block', sm: 'block', md: 'block', lg: 'block', xl: 'block'}}>
                  <div style={{paddingLeft: '10px', paddingRight: '0px', marginTop: '8px', display: "inline-flex", cursor: 'pointer'}} onClick={() => history.push('/')}>
                    <Logo mode={theme.palette.mode} />
                  </div>
                </Grid>
                <Grid item display={{ xs: 'block', sm: 'block', md: 'block', lg: 'block', xl: 'block'}}>
                  <div style={{paddingLeft: '10px', paddingRight: '0px', marginTop: '3px', display: "inline-flex", cursor: 'pointer'}} onClick={() => history.push('/')}>
                    <Brand width={111} height={48} mode={theme.palette.mode} />
                  </div>
                </Grid>
                {/* <Grid item display={{ xs: 'block', sm: 'block', md: 'block', lg: 'block', xl: 'block'}}>
                  <div style={{paddingLeft: '10px', paddingRight: '0px', marginTop: '4px', display: "inline-flex", cursor: 'pointer', letterSpacing: 0.9, color: theme.palette.mode==='light'?theme.palette.grey[900]:theme.palette.grey[50]}} onClick={() => history.push('/')}><h4 style={{ fontFamily: 'monospace', margin: '0px'}}>{brand.brandConfig['config'].dba}</h4></div>
                </Grid> */}
              </Grid>
              <Divider orientation='vertical' sx={{ marginLeft: '16px', marginRight: '8px' , height: '32px', borderWidth: '1px' }}/>
              {accountSwitcher}
              <div style={{
                  marginLeft: "auto",
                  marginRight: "0px",
                  paddingRight: '12px'
              }}>
                {/* <Notifications/> */}
                <IconButton
                  aria-label="open user menu"
                  onClick={openUserMenu}
                  edge="end"
                >
                  {FirebaseAuth.auth().currentUser.photoURL?(
                    <Avatar
                      alt={FirebaseAuth.auth().currentUser.displayName}
                      src={FirebaseAuth.auth().currentUser.photoURL}
                      sx={{
                        width: 24,
                        height: 24,
                        marginLeft: 0,
                        marginTop: 0,
                        marginBottom: 0,
                        marginRight: 0
                      }}
                    />
                  ):(
                    <AccountCircleIcon />
                  )}
                </IconButton>
              </div>
            </Toolbar>
          </AppBar>
          )}
          <Menu
            anchorEl={accountMenuAnchor}
            open={accountMenuOpen}
            onClose={closeAccountMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: "14px",
                border: "2px solid #d0d0d0",
                boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
                minWidth: 300,
                overflow: "hidden",
              },
            }}
          >
            {clientAccountOptions.map((account) => (
              <MenuItem
                key={account.id}
                onClick={() => handleSwitchClientAccount(account.id)}
                sx={{
                  py: 1.2,
                  px: 2.2,
                  backgroundColor: account.id === activeClientAccountId ? "#e7e7e7" : "transparent",
                  "&:hover": {
                    backgroundColor: account.id === activeClientAccountId ? "#e2e2e2" : "#f5f5f5",
                  },
                }}
              >
                <ListItemText
                  primary={account.name}
                  primaryTypographyProps={{
                    sx: {
                      color: "#283445",
                      fontSize: "14px",
                      fontWeight: account.id === activeClientAccountId ? 700 : 500,
                      lineHeight: 1.25,
                    },
                  }}
                />
              </MenuItem>
            ))}
          </Menu>
          <Menu
            anchorEl={userMenuAnchor}
            open={userMenuOpen}
            onClose={closeUserMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                mt: 1.25,
                width: { xs: "calc(100vw - 24px)", sm: 400 },
                maxWidth: "calc(100vw - 24px)",
                maxHeight: "calc(100dvh - 96px)",
                borderRadius: "18px",
                border: "1px solid #dadce0",
                boxShadow: "0 18px 48px rgba(60, 64, 67, 0.24)",
                overflow: "hidden",
              },
            }}
            MenuListProps={{
              component: "div",
              sx: { p: 0 },
            }}
          >
            {userMenuNode}
          </Menu>
          <DashboardSearchPalette
            open={searchPaletteOpen && searchEnabled}
            onClose={closeSearchPalette}
            role={normalizedRole}
          />
          <Box component="main" sx={{ flexGrow: 1, overflow:'hidden'}}>
            <div style={{position: 'relative', marginTop: isDashboardRoute ? 0 : `${APP_BAR_HEIGHT}px`}}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12} display={{ xs: 'block', sm: 'block', md: 'block', lg: 'block', xl: 'block'}}>
                <Box mt={0} mr={0} mb={0} style={{ textAlign: 'center'}}>
                  <BreadcrumbContext.Provider value={{setBreadcrumb}}>
                    <DashboardChromeProvider value={dashboardChrome}>
                      {children}
                    </DashboardChromeProvider>
                  </BreadcrumbContext.Provider>
                </Box>
              </Grid>
            </div>
          </Box>
        </Box>
      </ThemeProvider>
  );
}

export default Layout;
